import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { validatePassword } from '../../utils/password-validation';
import '../../styles/PasswordRequirementsFormatting.css';
import './Settings.css';
import api from '../../services/api';

const Settings = () => {
  const { user, loading, setIsAuthenticated, setUser } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [isDiscordLoading, setIsDiscordLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.fromPricing && bottomRef.current) {
      setTimeout(() => {
        bottomRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 0);
    }
  }, [location]);

  const handleConnectToDiscord = async () => {
    setIsDiscordLoading(true);
    try {
      const response = await api.get('/api/discord/auth');
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Discord connection error:', error);
      setError('Failed to connect to Discord');
    } finally {
      setIsDiscordLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    setIsPasswordLoading(true);
    try {
      const response = await api.post('api/change-password', {
        email: user.email,
        currentPassword,
        newPassword,
      });

      if (response.status === 200) {
        setMessage('Password has been updated successfully.');
        setError('');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(response?.data?.error || 'Failed to update password.');
        setMessage('');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      if (error?.response?.data?.error) {
        setError(error?.response?.data?.error);
      } else {
        setError('Something went wrong. Please try again.');
        setMessage('');
      }
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsLoading(true);
    try {
      await api.post('/api/logout');
      setIsAuthenticated(false);
      setUser(null);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsSubscriptionLoading(true);
    setError(null);

    try {
      const response = await api.get('/api/create-portal-session');
      const url = response?.data?.url;
      
      if (url) {
        window.location.href = url;
      } else {
        throw new Error(response?.data?.error || 'Failed to create portal session');
      }
      
    } catch (err) {
      console.error('Portal error details:', err);
      setError(`Subscription management error: ${err.message}`);
    } finally {
      setIsSubscriptionLoading(false);
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string) return 'Inactive';
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  if (loading) {
    return <div className="spinner-container"><div className="spinner" /></div>;
  }

  return (
    <div className="settings-container">
      <section className="settings-section">
        <h2>Account Information</h2>
        {(user?.plan_type === null || user?.plan_type === undefined ||
          user?.renewal_date === null || user?.renewal_date === undefined) ? (
          <div className="inactive-subscription-container">
            <button onClick={handleManageSubscription} className="inactive-subscription">
              Subscription is inactive, renew here
            </button>
          </div>
        ) : (user?.cancel_at_period_end ? (
          <div className="inactive-subscription-container">
            <button onClick={handleManageSubscription} className="inactive-subscription">
              Subscription expiring soon, renew here
            </button>
          </div>
        ) : null)}
        <div className="info-item">
          <label>Email:</label>
          <p>{user?.email}</p>
        </div>
        <div className="info-item">
          <button
            onClick={handleConnectToDiscord}
            className={`connect-discord-button button-loading ${user.discord_user_id ? 'connected' : ''}`}
            disabled={isDiscordLoading}
          >
            {isDiscordLoading ? <div className="spinner"></div> : user.discord_user_id ? 'Discord Connected' : 'Connect to Discord'}
          </button>
        </div>
      </section>

      <section className="settings-section">
        <h2>Change Password</h2>
        <form onSubmit={handlePasswordChange} className="password-form">
          <div className="input-group password-group">
            <input
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <span
              className="toggle-password"
              onClick={() => setShowCurrentPassword(!showCurrentPassword)}
            >
              {showCurrentPassword ? (
                <i className="fas fa-eye" />
              ) : (
                <i className="fas fa-eye-slash" />
              )}
            </span>
          </div>
          <div className="input-group password-group">
            <input
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <span
              className="toggle-password"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? (
                <i className="fas fa-eye" />
              ) : (
                <i className="fas fa-eye-slash" />
              )}
            </span>
          </div>
          <div className="input-group password-group">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <span
              className="toggle-password"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? (
                <i className="fas fa-eye" />
              ) : (
                <i className="fas fa-eye-slash" />
              )}
            </span>
          </div>
          <button type="submit" className="update-button button-loading" disabled={isPasswordLoading}>
            {isPasswordLoading ? <div className="spinner"></div> : 'Update Password'}
          </button>
        </form>
        {error && <p className="error-message">{error}</p>}
        {message && <p className="success-message">{message}</p>}
        <div className="password-requirements">
          <p>Password must contain:</p>
          <ul>
            <li>Between 8 and 25 characters</li>
            <li>Uppercase and lowercase letters</li>
            <li>At least one number</li>
          </ul>
        </div>
      </section>

      <section className="settings-section">
        <h2>Membership</h2>
        <div className="info-item">
          <label>Membership plan:</label>
          <p>
            {
              (!user?.plan_type || !user?.renewal_date) && user?.active === false
                ? 'Inactive'
                : capitalizeFirstLetter(user?.plan_type)
            }
          </p>
        </div>
        <div className="info-item">
          <label>Renewal Date:</label>
          <p>
            {
              user?.cancel_at_period_end === true
                ? 'Canceled'
                : !user?.renewal_date
                  ? 'NA'
                  : formatDate(user?.renewal_date)
            }
          </p>
        </div>
        <button
          className="manage-button button-loading"
          onClick={handleManageSubscription}
          disabled={isSubscriptionLoading}
        >
          {isSubscriptionLoading ? <div className="spinner"></div> : 'Manage Subscription'}
        </button>
      </section>

      <button onClick={handleLogout} className="logout-button button-loading" disabled={isLoading}>
        {isLoading ? <div className="spinner"></div> : 'Log Out'}
      </button>
      <div ref={bottomRef}></div>
    </div>
  );
};

export default Settings;